import { useRef, useCallback, useEffect } from 'react';

interface UseSmoothScrollOptions {
  enabled?: boolean;
  scrollThreshold?: number;
  scrollDuration?: number;
  easing?: (t: number) => number;
}

export const useSmoothScroll = (options: UseSmoothScrollOptions = {}) => {
  const {
    enabled = true,
    scrollThreshold = 100, // Only scroll if user is within 100px of bottom
    scrollDuration = 300, // 300ms scroll duration
    easing = (t: number) => t * (2 - t) // ease-out easing
  } = options;

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollAnimationRef = useRef<number | null>(null);
  const lastScrollTimeRef = useRef(0);
  const isStreamingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user is near bottom of scroll area
  const isNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    
    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    return (scrollHeight - scrollTop - clientHeight) <= scrollThreshold;
  }, [scrollThreshold]);

  // Smooth scroll to bottom with custom animation
  const scrollToBottom = useCallback((force = false, isStreaming = false) => {
    if (!enabled || !messagesEndRef.current) return;
    
    // Only scroll if user is near bottom or forced
    if (!force && !isNearBottom()) return;

    const now = performance.now();
    const timeSinceLastScroll = now - lastScrollTimeRef.current;
    
    // Different throttling for streaming vs regular updates
    const throttleTime = isStreaming ? 8 : 16; // More frequent updates during streaming
    if (!force && timeSinceLastScroll < throttleTime) return;

    lastScrollTimeRef.current = now;
    isStreamingRef.current = isStreaming;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    // For streaming, use immediate scroll with minimal animation
    if (isStreaming) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
      return;
    }

    // Cancel any existing scroll animation
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    const startTime = performance.now();
    const startScrollTop = scrollContainerRef.current?.scrollTop || 0;
    const targetScrollTop = scrollContainerRef.current?.scrollHeight || 0;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / scrollDuration, 1);
      const easedProgress = easing(progress);

      if (scrollContainerRef.current) {
        const currentScrollTop = startScrollTop + (targetScrollTop - startScrollTop) * easedProgress;
        scrollContainerRef.current.scrollTop = currentScrollTop;
      }

      if (progress < 1) {
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationRef.current = null;
        isScrollingRef.current = false;
      }
    };

    isScrollingRef.current = true;
    scrollAnimationRef.current = requestAnimationFrame(animateScroll);
  }, [enabled, isNearBottom, scrollDuration, easing]);

  // Set up scroll container reference
  const setScrollContainer = useCallback((element: HTMLElement | null) => {
    scrollContainerRef.current = element;
  }, []);

  // Handle scroll events to detect user scrolling
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Clear any pending scroll to bottom
      clearTimeout(scrollTimeout);
      
      // If user scrolls up, disable auto-scroll temporarily
      if (!isNearBottom()) {
        scrollTimeout = setTimeout(() => {
          // Re-enable auto-scroll after user stops scrolling
        }, 1000);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isNearBottom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, []);

  return {
    messagesEndRef,
    setScrollContainer,
    scrollToBottom,
    isNearBottom
  };
};
